import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import css from "./style.module.css";
import BackSection from "../Services/BackSection";
import { useOktaConfig } from "../../Context/CredentialsContext";
import Step1Company from "./Step1Company";
import Step2Integrations from "./Step2Integrations";
import Step3MarketingSeeds from "./Step3MarketingSeeds";
import ReviewStep from "./ReviewStep";
import VerifyScreen from "./VerifyScreen";
import { step1Schema, step2Schema, step3Schema } from "./validationSchemas";
import { bootstrapCustomer } from "./onboardingApi";

const STORAGE_KEY = "onboardWizard:state";

const DEFAULT_VALUES = {
  companyName: "",
  companyDomain: "",
  senderEmail: "",
  file: null,
  oktaDomain: "",
  oktaClientID: "",
  sfUsername: "",
  sfPassword: "",
  sfSecurityToken: "",
  documentTypes: ["Brochure", "Email", "Social Post", "Print Ad"],
  mediaChannels: ["Email", "Social", "Web", "Print"],
  geographies: ["US", "CA", "UK"],
  priorities: ["High", "Medium", "Low"],
};

const STEPS = [
  { key: "company", label: "Company", schema: step1Schema },
  { key: "integrations", label: "Integrations", schema: step2Schema },
  { key: "marketing", label: "Marketing", schema: step3Schema },
  { key: "review", label: "Review", schema: null },
];

function loadPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist(state) {
  try {
    // file (File object) is not serializable; strip it.
    const { values, step } = state;
    const { file, ...serializable } = values;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ step, values: serializable })
    );
  } catch {
    // sessionStorage can throw in private mode; ignore.
  }
}

function clearPersisted() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function Stepper({ step }) {
  return (
    <div className={css.stepperBar}>
      {STEPS.map((s, idx) => {
        const isActive = idx === step;
        const isDone = idx < step;
        const dotClass = isDone
          ? `${css.stepDot} ${css.stepDotDone}`
          : isActive
          ? `${css.stepDot} ${css.stepDotActive}`
          : css.stepDot;
        const labelClass = isActive
          ? `${css.stepLabel} ${css.stepLabelActive}`
          : css.stepLabel;
        return (
          <React.Fragment key={s.key}>
            <div className="d-flex align-items-center gap-2">
              <span className={dotClass}>{isDone ? "✓" : idx + 1}</span>
              <span className={labelClass}>{s.label}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <span
                className={`${css.stepConnector} ${
                  isDone ? css.stepConnectorDone : ""
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OnboardCustomer() {
  const { loginToken } = useOktaConfig();
  const navigate = useNavigate();

  const persisted = useMemo(loadPersisted, []);
  const [step, setStep] = useState(persisted?.step ?? 0);
  const [companyId, setCompanyId] = useState(null);
  const [duplicateField, setDuplicateField] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const initialValues = useMemo(
    () => ({ ...DEFAULT_VALUES, ...(persisted?.values || {}) }),
    [persisted]
  );

  const valuesRef = useRef(initialValues);
  const stepRef = useRef(step);

  useEffect(() => {
    stepRef.current = step;
    persist({ values: valuesRef.current, step });
  }, [step]);

  const mutation = useMutation({
    mutationFn: (vars) => bootstrapCustomer(vars),
    onSuccess: (data) => {
      setCompanyId(data?.companyId);
      setStep(STEPS.length); // advance to verify screen
      clearPersisted();
    },
    onError: (err) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      if (status === 409) {
        const field = /domain/i.test(message || "")
          ? "companyDomain"
          : "companyName";
        setDuplicateField(field);
        setSubmitError(message || "A company with these details already exists.");
        setStep(0);
      } else {
        setSubmitError(message || "Failed to create the customer.");
      }
    },
  });

  const buildPayload = (values) => {
    const payload = {
      company: {
        companyName: values.companyName.trim(),
        companyDomain: values.companyDomain.trim(),
        oktaDomain: values.oktaDomain.trim(),
        oktaClientID: values.oktaClientID.trim(),
        sfUsername: values.sfUsername.trim(),
        sfPassword: values.sfPassword,
        sfSecurityToken: values.sfSecurityToken.trim(),
        senderEmail: values.senderEmail.trim(),
        accessMarketing: true,
      },
      marketing: {
        documentTypes: values.documentTypes || [],
        mediaChannels: values.mediaChannels || [],
        geographies: values.geographies || [],
        priorities: values.priorities || [],
      },
    };
    if (values.file) {
      payload.company.file = values.file;
    }
    return payload;
  };

  const onSubmitReview = (values) => {
    setSubmitError(null);
    setDuplicateField(null);
    mutation.mutate({ loginToken, payload: buildPayload(values) });
  };

  const onStartOver = () => {
    clearPersisted();
    setStep(0);
    setCompanyId(null);
    setDuplicateField(null);
    setSubmitError(null);
    valuesRef.current = DEFAULT_VALUES;
    navigate(0); // simple reset — re-mount the route
  };

  const isVerify = step === STEPS.length;

  return (
    <section className={css.wizardSection}>
      <BackSection pageName="Onboard Customer" to="/policies" />

      <div className="row mt-3">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className={css.wizardCard}>
            {!isVerify && <Stepper step={step} />}

            {isVerify ? (
              <VerifyScreen companyId={companyId} onStartOver={onStartOver} />
            ) : (
              <Formik
                initialValues={initialValues}
                validationSchema={STEPS[step].schema || undefined}
                onSubmit={(values, helpers) => {
                  if (step < STEPS.length - 1) {
                    setStep(step + 1);
                    helpers.setSubmitting(false);
                    helpers.setTouched({});
                  } else {
                    onSubmitReview(values);
                    helpers.setSubmitting(false);
                  }
                }}
                enableReinitialize={false}
                validateOnMount={false}
              >
                {({ values, isSubmitting }) => {
                  valuesRef.current = values;
                  persist({ values, step });
                  return (
                    <Form noValidate>
                      {step === 0 && (
                        <Step1Company duplicateField={duplicateField} />
                      )}
                      {step === 1 && <Step2Integrations />}
                      {step === 2 && <Step3MarketingSeeds />}
                      {step === 3 && <ReviewStep values={values} />}

                      {submitError && step === 3 && (
                        <div className="alert alert-danger mt-3 mb-0">
                          {submitError}
                        </div>
                      )}

                      <div className="d-flex justify-content-between mt-4">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setStep(Math.max(0, step - 1))}
                          disabled={step === 0 || mutation.isPending}
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          className={`btn ${css.btnPrimary}`}
                          disabled={isSubmitting || mutation.isPending}
                        >
                          {step === STEPS.length - 1
                            ? mutation.isPending
                              ? "Creating customer…"
                              : "Create customer"
                            : "Next"}
                          {mutation.isPending && (
                            <span
                              className="spinner-border spinner-border-sm ms-2"
                              role="status"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
